import { ExternalConnectionContact, ID } from '@griply/types';
import { dedupeContacts } from '@griply/utils';
import { QueryClient, useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createContainedQueryFn } from 'shared/lib/@tanstack-query';
import { getExternalConnectionContacts } from 'shared/services/backend-api';

export const queryKey = 'external-connection-contacts';

export const createExternalConnectionContactsQueryFn = (
  queryClient: QueryClient,
  externalConnectionId: ID,
) =>
  createContainedQueryFn<ExternalConnectionContact>({
    queryClient,
    queryKey: [queryKey, externalConnectionId],
    fetch: () => getExternalConnectionContacts(externalConnectionId),
    source: 'external-connections-get-contacts',
  });

export type UseExternalConnectionsContactsQueriesOptions = {
  enabled?: boolean;
};

// One suggestion pool across all capable connections, deduped by email. No staleTime
// override: the global Infinity default makes contacts a fetch-once-per-session value —
// the first dialog open pays the callables, every later open reads the cache.
export const useExternalConnectionsContactsQueries = (
  connectionIds: ID[],
  { enabled = true }: UseExternalConnectionsContactsQueriesOptions = {},
): ExternalConnectionContact[] => {
  const queryClient = useQueryClient();
  const queries = useQueries({
    queries: connectionIds.map((id) => ({
      queryKey: [queryKey, id],
      queryFn: createExternalConnectionContactsQueryFn(queryClient, id),
      // pending would suspend the root-mounted EventFormProvider and blank the app shell
      placeholderData: [],
      enabled,
    })),
  });

  return useMemo(
    () => dedupeContacts(queries.flatMap((query) => query.data ?? [])),
    [queries],
  );
};
